import { useAuth } from '@/auth/auth-context'
import { ModeToggle } from '@/components/mode-toggle'
import { cn } from '@/utils/cn'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@blogging/ui'
import { Separator } from '@radix-ui/react-separator'
import {
  BarChart2,
  Building2,
  ChevronLeft,
  ExternalLink,
  HelpCircle,
  Home,
  LogOut,
  Menu,
  MessageCircle,
  Settings,
  Tag,
  User,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useSite } from '../../hooks/use-site'
import SlashIcon from '../icons/slash'
import { SidebarContainer } from './container'
import NavLogoIcon from './nav-logo-icon'

function navigation(siteId: string) {
  const getUrl = (path: string) => `/${siteId}${path}`

  return [
    { name: 'Dashboard', href: getUrl(''), icon: Home },
    { name: 'Pages', href: getUrl('/pages'), icon: BarChart2 },
    { name: 'Posts', href: getUrl('/posts'), icon: Building2 },
    { name: 'Tags', href: getUrl('/tags'), icon: Tag },
  ]
}

const bottomNavigation = (
  siteId: string,
  siteUrlWithScheme: string,
  isAdmin: boolean,
): {
  name: string
  href: string
  icon: React.ElementType
  type: 'external' | 'internal'
}[] => {
  const getUrl = (path: string) => `/${siteId}${path}`

  const items = []

  // Only show Settings to admins
  if (isAdmin) {
    items.push({
      name: 'Settings',
      href: getUrl('/settings'),
      icon: Settings,
      type: 'internal' as const,
    })
  }

  items.push(
    {
      name: 'View Site',
      href: siteUrlWithScheme,
      icon: ExternalLink,
      type: 'external' as const,
    },
    {
      name: 'All Sites',
      href: '/',
      icon: Home,
      type: 'internal' as const,
    },
    {
      name: 'Help',
      href: 'https://blogging.dev',
      icon: HelpCircle,
      type: 'external' as const,
    },
    {
      name: 'Feedback',
      href: 'mailto:ngtrvu@gmail.com',
      icon: MessageCircle,
      type: 'internal' as const,
    },
  )

  return items
}

export function Sidebar() {
  const location = useLocation()
  const pathname = location.pathname
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const { siteId } = useParams()
  const { siteUrlWithScheme, isAdmin, isLoading, site } = useSite()
  const navigationItems = navigation(siteId as string)
  const bottomNavigationItems = bottomNavigation(
    siteId as string,
    siteUrlWithScheme as string,
    isAdmin,
  )
  const { user, logout } = useAuth()

  const NavItem = ({
    item,
    isBottom = false,
  }: {
    item: any
    isBottom?: boolean
  }) => (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        {item.type === 'external' ? (
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
              pathname === item.href
                ? 'bg-secondary text-secondary-foreground'
                : 'text-muted-foreground hover:bg-secondary hover:text-secondary-foreground',
              isCollapsed && 'justify-center px-2',
            )}
          >
            <item.icon className={cn('h-4 w-4', !isCollapsed && 'mr-3')} />
            {!isCollapsed && <span>{item.name}</span>}
          </a>
        ) : (
          <Link
            to={item.href}
            className={cn(
              'flex items-center rounded-md px-3 py-2 text-sm font-medium transition-colors',
              pathname === item.href
                ? 'bg-secondary text-secondary-foreground'
                : 'text-muted-foreground hover:bg-secondary hover:text-secondary-foreground',
              isCollapsed && 'justify-center px-2',
            )}
          >
            <item.icon className={cn('h-4 w-4', !isCollapsed && 'mr-3')} />
            {!isCollapsed && <span>{item.name}</span>}
          </Link>
        )}
      </TooltipTrigger>
      {isCollapsed && (
        <TooltipContent side="right" className="flex items-center gap-4">
          {item.name}
        </TooltipContent>
      )}
    </Tooltip>
  )

  if (isLoading) {
    return (
      <SidebarContainer>
        <div className="hidden lg:flex flex-col w-72 sticky top-6 h-[calc(100vh-3rem)] bg-background">
          {/* Header */}
          <div className="flex items-center gap-2 px-2 py-2">
            <Skeleton className="h-8 w-8 rounded-md shrink-0" />
            <Skeleton className="h-4 w-4 shrink-0" />
            <Skeleton className="h-6 w-32" />
          </div>

          {/* Nav items */}
          <div className="flex-1 min-h-0 overflow-hidden px-2 py-4 space-y-1">
            <div className="flex items-center gap-3 px-3 py-2 rounded-md">
              <Skeleton className="h-4 w-4 shrink-0" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="my-2 border-t border-border" />
            <div className="flex items-center gap-3 px-3 py-2 rounded-md">
              <Skeleton className="h-4 w-4 shrink-0" />
              <Skeleton className="h-4 w-20" />
            </div>
          </div>

          {/* User profile */}
          <div className="border-t border-border p-2">
            <div className="flex items-center gap-3 px-3 py-2">
              <Skeleton className="h-7 w-7 rounded-full shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-28" />
                <Skeleton className="h-2.5 w-16" />
              </div>
            </div>
          </div>
        </div>
      </SidebarContainer>
    )
  }

  return (
    <TooltipProvider>
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-10 bg-black/50"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      <SidebarContainer>
        <button
          className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-background rounded-md shadow-md"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          aria-label="Toggle sidebar"
        >
          {isMobileOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>
        <div
          className={cn(
            'fixed inset-y-0 z-20 flex flex-col bg-background transition-all duration-300 ease-in-out lg:sticky lg:top-6 lg:h-[calc(100vh-3rem)]',
            isCollapsed ? 'w-[72px]' : 'w-72',
            isMobileOpen
              ? 'translate-x-0'
              : '-translate-x-full lg:translate-x-0',
          )}
        >
          <div className="px-3">
            <div
              className={cn(
                'flex items-center gap-2 h-8',
                isCollapsed && 'justify-center',
              )}
            >
              {!isCollapsed && (
                <>
                  <Link to="/" className="flex items-center shrink-0">
                    <NavLogoIcon />
                  </Link>
                  <SlashIcon className="h-6 w-6 shrink-0 text-gray-500" />
                  <Link to={`/${siteId}`} className="flex items-center min-w-0">
                    <span className="font-medium tracking-tight text-lg truncate leading-none">
                      {site?.name}
                    </span>
                  </Link>
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'hidden lg:flex ml-auto h-8 w-8',
                  isCollapsed && 'ml-0',
                )}
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                <ChevronLeft
                  className={cn(
                    'h-4 w-4 transition-transform',
                    isCollapsed && 'rotate-180',
                  )}
                />
                <span className="sr-only">
                  {isCollapsed ? 'Expand' : 'Collapse'} Sidebar
                </span>
              </Button>
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <nav className="flex-1 space-y-1 py-4">
              {navigationItems.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </nav>
            <div className="border-t border-border p-2">
              <Separator />
            </div>
            <nav className="flex-1 space-y-1 py-4">
              {bottomNavigationItems.map((item) => (
                <NavItem key={item.name} item={item} isBottom />
              ))}
            </nav>
          </div>
          <div className="border-t border-border p-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    'flex items-center gap-3 w-full rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-secondary',
                    isCollapsed && 'justify-center px-2',
                  )}
                >
                  {user?.image ? (
                    <img
                      src={user.image}
                      alt={user.name || 'User'}
                      className="h-7 w-7 rounded-full"
                    />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  {!isCollapsed && (
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium truncate">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side={isCollapsed ? 'right' : 'top'}
                align="start"
                className="w-56"
              >
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium truncate">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/account" className="cursor-pointer">
                    <Settings className="h-4 w-4 mr-2" />
                    Account Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    if (confirm('Are you sure you want to log out?')) {
                      logout()
                    }
                  }}
                  className="cursor-pointer"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div
              className={cn(
                'flex items-center gap-2 mt-2 pt-2 border-t border-border',
                isCollapsed ? 'justify-center' : 'justify-between px-3',
              )}
            >
              {!isCollapsed && (
                <span className="text-xs text-muted-foreground">
                  © {new Date().getFullYear()} blogging.dev
                </span>
              )}
              <ModeToggle />
            </div>
          </div>
        </div>
      </SidebarContainer>
    </TooltipProvider>
  )
}
